import dayjs from 'dayjs';
import { TrendGranularity } from './types';
import { GRANULARITY_OPTIONS } from './constants';

interface GranularityOption {
  value: TrendGranularity;
  label: string;
  maxRange: number;
}

/**
 * 获取时间粒度对应的最大范围
 */
export const getMaxRange = (granularity: TrendGranularity, options?: GranularityOption[]): number => {
  const opts = options || GRANULARITY_OPTIONS;
  const option = opts.find(o => o.value === granularity);
  return option?.maxRange ?? 30;
};

/**
 * 获取默认日期范围
 */
export const getDefaultDateRange = (
  granularity: TrendGranularity,
  options?: GranularityOption[]
): [dayjs.Dayjs, dayjs.Dayjs] => {
  const now = dayjs();
  const maxRange = getMaxRange(granularity, options);

  switch (granularity) {
    case TrendGranularity.HOUR:
      return [now.subtract(maxRange, 'day').startOf('day'), now.endOf('day')];
    case TrendGranularity.DAY:
      return [now.subtract(maxRange, 'day').startOf('day'), now.endOf('day')];
    case TrendGranularity.MONTH:
      return [now.subtract(maxRange, 'month').startOf('month'), now.endOf('month')];
    default:
      return [now.subtract(6, 'day').startOf('day'), now.endOf('day')];
  }
};

/**
 * 禁用日期判断函数
 * @param current 当前日期
 * @param granularity 时间粒度
 * @param from 起始日期（可选）
 * @param options 粒度选项（可选，默认使用全局配置）
 */
export const getDisabledDate = (
  current: dayjs.Dayjs,
  granularity: TrendGranularity,
  from?: dayjs.Dayjs | null,
  options?: GranularityOption[]
): boolean => {
  // 禁用未来日期
  if (current > dayjs().endOf('day')) {
    return true;
  }

  const maxRange = getMaxRange(granularity, options);

  // 如果有起始日期，禁用超出范围的日期
  if (from) {
    const diff = granularity === TrendGranularity.MONTH ? current.diff(from, 'month') : current.diff(from, 'day');
    return Math.abs(diff) > maxRange;
  }

  // 没有起始日期时，限制可选范围在 maxRange 内（从今天往前推 maxRange）
  const earliest =
    granularity === TrendGranularity.MONTH
      ? dayjs().subtract(maxRange, 'month').startOf('month')
      : dayjs().subtract(maxRange, 'day').startOf('day');
  if (current < earliest) {
    return true;
  }

  return false;
};

/**
 * 格式化时间显示
 */
export const formatTime = (time: string, granularity: TrendGranularity): string => {
  switch (granularity) {
    case TrendGranularity.HOUR:
      return dayjs(time).format('YYYY-MM-DD HH:mm');
    case TrendGranularity.DAY:
      return dayjs(time).format('YYYY-MM-DD');
    case TrendGranularity.MONTH:
      return dayjs(time).format('YYYY-MM');
    default:
      return time;
  }
};

/**
 * 格式化请求参数时间
 */
export const formatRequestTime = (dateRange: [dayjs.Dayjs, dayjs.Dayjs], granularity: TrendGranularity) => {
  const [start, end] = dateRange;

  switch (granularity) {
    case TrendGranularity.MONTH:
      return {
        startTime: start.startOf('month').valueOf(),
        endTime: end.endOf('month').valueOf(),
      };
    case TrendGranularity.HOUR:
    case TrendGranularity.DAY:
    default:
      return {
        startTime: start.startOf('day').valueOf(),
        endTime: end.endOf('day').valueOf(),
      };
  }
};

/**
 * 根据维度获取默认时间值
 */
export const getDefaultTimeValue = (dimension: 'MONTH' | 'YEAR'): string => {
  const now = dayjs();
  return dimension === 'MONTH' ? now.format('YYYY-MM') : now.format('YYYY');
};
